import PropTypes from "prop-types";
import { createContext, useContext, useState } from "react";
import api from "../config/api";
import { toast } from "react-toastify";
import { routes } from "../../routes";
import { useNavigate, useLocation } from "react-router";
import { removeUserCookies, setUserCookies } from "../config/apiConfig";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [accessPaymentRoutes, setAccessPaymentRoutes] = useState(localStorage.getItem("accessPaymentRoutes")|| false);
  const [couponCodeDiscount, setCouponCodeDiscount] = useState(0);
  const [paymentInfo,setPaymentInfo] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const userLogin = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.userLogin(request);
      if (response?.success) {
        toast.success(response?.message || "User signin failed!");
        setUserCookies(response.data);
        localStorage.setItem("user",JSON.stringify(response.data))
      }
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const userGoogleLogin = async (request) => {
    try {
      const response = await api.auth.userGoogleLogin(request);
      if (response?.success) {
        toast.success(response?.message || "User signin failed!");
        setUserCookies(response.data);
        if (location?.state?.redirectTo) {
          navigate(location.state.redirectTo, { state: location.state.redirectState });
        } else {
          navigate(routes.HOME);
        }
      }
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong!");
    }
  };

  const userFacebookLogin = async (request) => {
    try {
      const response = await api.auth.userFacebookLogin(request);
      if (response?.success) {
        toast.success(response?.message || "User signin failed!");
        setUserCookies(response.data);
        if (location?.state?.redirectTo) {
          navigate(location.state.redirectTo, { state: location.state.redirectState });
        } else {
          navigate(routes.HOME);
        }
      }
      return response;
    } catch (error) {
      toast.error(error?.response?.message || "Something went wrong!");
    }
  };

  const socialAuthFlow = async (socialData) => {
    setLoading(true);
    try {
      const loginPayload = {
        provider: socialData.provider,
        provider_id: socialData.provider_id
      };
      
      let response;
      try {
        response = await api.auth.socialLogin(loginPayload);
      } catch (loginError) {
        if (loginError?.response?.status === 404) {
          const registerPayload = {
            provider: socialData.provider,
            provider_id: socialData.provider_id,
            email_id: socialData.email_id,
            first_name: socialData.first_name || "Social",
            last_name: socialData.last_name || "User",
            type: socialData.type || "USER",
            mobile: socialData.mobile || "0",
            countrycode: socialData.countrycode || "0",
            languages: socialData.languages || "English"
          };
          
          const regResponse = await api.auth.socialRegister(registerPayload);
          if (regResponse?.success) {
            response = await api.auth.socialLogin(loginPayload);
          } else {
            throw new Error(regResponse?.message || "Social registration failed");
          }
        } else {
          throw loginError;
        }
      }

      if (response?.success) {
        toast.success(response?.message || "Logged in successfully!");
        setUserCookies(response.data);
        localStorage.setItem("user", JSON.stringify(response.data));
        
        if (location?.state?.redirectTo) {
          navigate(location.state.redirectTo, { state: location.state.redirectState });
        } else {
          navigate(routes.HOME);
        }
        return response;
      } else {
        toast.error(response?.message || "Authentication failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Social login failed");
      console.error("Social login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.register(request);
      if (response?.success) {
        toast.success(response?.message || "User signup failed!");
        if (location?.state?.redirectTo) {
          navigate(routes.SIGNIN, {
            state: {
              redirectTo: location.state.redirectTo,
              redirectState: location.state.redirectState
            }
          });
        } else {
          navigate(routes.SIGNIN);
        }
      }
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message  || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const subscribeUs = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.subscribeUs(request);
      if (response?.success) {
        setLoading(false);
        toast.success(response?.message);
      }
      return response;
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    };
  }

  const userSignout = () => {
    try {
      toast.success("Logged out successfully!");
      removeUserCookies();
      localStorage.clear();
      navigate(routes.SIGNIN);
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const applyCouponCode = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.applyCouponCode(request);
      if (response?.success) {
        toast.success(response?.message);
        setLoading(false);
        setCouponCodeDiscount(response?.data);
      } else {
        toast.error(response?.message);
        setCouponCodeDiscount(0);
      }
    } catch (error) {
      setCouponCodeDiscount(0);
      toast.error(error?.response?.message || "Coupon code is not valid!");
    } finally {
      setLoading(false);
    }
  };

  const CreatePaymentInfo = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.paymentData(request);
      if (response?.success) {
        // toast.success(response?.message);
        setPaymentInfo(response?.data);
        setLoading(false);
      }
      return response;
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.updateUser(request);
      if (response?.success) {
        toast.success(response?.message);
        setLoading(false);
      }
      return response;
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (request) => {
    setLoading(true);
    try {
      const response = await api.auth.passwordChange(request);
      if (response?.success) {
        toast.success(response?.message);
        setLoading(false);
      }
      return response;
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    setLoading(true);
    try {
      const response = await api.auth.verifyProfile(token);
      if (response?.success) {
        toast.success(response?.message || "Email verified successfully!");
      }
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Verification link is invalid or expired!");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const contextValue = {
    loading,
    userLogin,
    signUp,
    subscribeUs,
    userSignout,
    userGoogleLogin,
    userFacebookLogin,
    socialAuthFlow,
    applyCouponCode,
    couponCodeDiscount,
    setCouponCodeDiscount,
    accessPaymentRoutes,
    setAccessPaymentRoutes,
    setPaymentInfo,
    paymentInfo,
    CreatePaymentInfo,
    updateProfile,
    changePassword,
    verifyEmail
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const useAuth = () => useContext(AuthContext);
